<template>
    <component :is="link ? 'router-link' : 'div'" :to="link ?? null" class="score-preview">
        <div
            class="score-preview__score"
            v-if="
                props.event.liveNow &&
                (eventScore?.redCardsHome || eventScore.scoreHome || eventScore.scoreAway || eventScore?.redCardsAway)
            "
        >
            <ColorCard v-if="Number(eventScore?.redCardsHome) > 0" :value="eventScore?.redCardsHome" />
            <ScoreSticker
                v-if="eventScore?.scoreHome || eventScore?.scoreAway"
                :home="eventScore.scoreHome"
                :away="eventScore.scoreAway"
            />
            <ColorCard v-if="Number(eventScore?.redCardsAway) > 0" :value="eventScore?.redCardsAway" />
        </div>
        <Tooltip class="score-preview__time" :text="tooltipText" show-only-on-overflow>
            <div class="time-played" v-if="isTournamentSport(event.drilldownNodes?.[2]?.code) && !prematch">
                Probíhá
            </div>
            <span v-if="event?.liveNow">
                <span v-if="eventTime?.minutesPlayed && eventTime?.minutesOvertime">
                    {{ eventTime.minutesPlayed }}'+{{ eventTime.minutesOvertime }} -
                </span>
                <span v-else-if="eventTime?.minutesPlayed">{{ eventTime.minutesPlayed }}' - </span>
                <span v-if="eventTime?.periodNumber || eventTime?.periodName">
                    {{ eventTime.periodNumber }}. {{ eventTime.periodName }}
                </span>
            </span>
            <div v-else-if="prematch" class="time-played">
                <span v-if="eventTime?.startDate">
                    {{ eventTime.startDate }}
                    <template v-if="eventTime?.startYear"> &nbsp;{{ eventTime.startYear }} </template>
                </span>
                <span v-if="eventTime?.startTime">{{ eventTime.startTime }}</span>
                <span v-if="displayLeague">{{ leagueName }}</span>
            </div>
            <template v-if="eventPeriods?.length && event.liveNow">
                <span v-if="eventTime?.periodNumber && eventTime?.periodName"> - </span>
                <template v-for="(period, index) in eventPeriods" :key="index">
                    <span v-if="index !== eventPeriods?.length - 1">
                        <span
                            :class="{
                                'score-preview__time--orange': period?.homeColor === 'orange',
                                'score-preview__time--blue': period?.homeColor === 'blue',
                            }"
                        >
                            {{ period?.scoreHome ?? 0 }} </span
                        >:<span
                            :class="{
                                'score-preview__time--orange': period?.awayColor === 'orange',
                                'score-preview__time--blue': period?.awayColor === 'blue',
                            }"
                        >
                            {{ period?.scoreAway ?? 0 }} </span
                        >,
                    </span>
                    <span v-else>
                        <span
                            :class="{
                                'score-preview__time--orange': period?.homeColor === 'orange',
                                'score-preview__time--blue': period?.homeColor === 'blue',
                            }"
                        >
                            {{ period?.scoreHome ?? 0 }} </span
                        ><span
                            :class="{
                                'score-preview__time--orange':
                                    period?.homeColor === 'orange' && period?.awayColor === 'orange',
                                'score-preview__time--blue':
                                    period?.homeColor === 'blue' && period?.awayColor === 'blue',
                            }"
                            >:</span
                        ><span
                            :class="{
                                'score-preview__time--orange': period?.awayColor === 'orange',
                                'score-preview__time--blue': period?.awayColor === 'blue',
                            }"
                        >
                            {{ period?.scoreAway ?? 0 }}
                        </span>
                        <span
                            v-if="period?.pointsHome || period?.pointsAway"
                            :class="{
                                'score-preview__time--orange':
                                    period?.homeColor === 'orange' && period?.awayColor === 'orange',
                                'score-preview__time--blue':
                                    period?.homeColor === 'blue' && period?.awayColor === 'blue',
                            }"
                        >
                            ({{ period?.starHome ? "*" : "" }}{{ period?.pointsHome }}:{{ period?.pointsAway
                            }}{{ period?.starAway ? "*" : "" }})
                        </span>
                    </span>
                </template>
            </template>
        </Tooltip>
    </component>
</template>

<script lang="ts" setup generic="T extends EventOptionalDrilldown">
import { computed, toRefs } from "vue";

import ColorCard from "@/shared/components/atoms/ColorCard.vue";
import ScoreSticker from "@/shared/components/atoms/ScoreSticker.vue";
import Tooltip from "@/shared/components/atoms/Tooltip.vue";

import useEventScoreAndStatus from "@/shared/composables/use-event-score-and-status";

import { EventOptionalDrilldown } from "@/shared/model/event";
import useTournamentSports from "@/shared/composables/use-tournament-outrights";

const { isTournamentSport } = useTournamentSports();

const props = withDefaults(
    defineProps<{
        event: T;
        prematch?: boolean;
        displayLeague?: boolean;
        link?: any;
    }>(),
    {
        prematch: false,
        displayLeague: false,
    },
);

const propsRefs = toRefs(props);
const leagueName = computed(() => props.event.drilldownNodes[0]?.name);

const { eventScore, eventTime, eventPeriods } = useEventScoreAndStatus(propsRefs.event);

const tooltipText = computed(() => {
    const score = `${eventScore.value?.scoreHome}:${eventScore.value?.scoreAway}`;

    if (props.prematch) {
        let timePlayed = "";

        timePlayed = `${eventTime.value?.startDate}`;

        if (eventTime.value?.startYear) {
            timePlayed += ` ${eventTime.value.startYear}`;
        }

        timePlayed += ` | ${eventTime.value?.startTime}`;

        if (props.displayLeague) {
            timePlayed += ` | ${leagueName.value}`;
        }

        return timePlayed;
    }

    let timePlayed = "";

    if (eventTime.value?.minutesPlayed > 0) {
        if (eventTime.value?.minutesOvertime > 0) {
            timePlayed = `${eventTime.value?.minutesPlayed}'+${eventTime.value?.minutesOvertime} - `;
        } else {
            timePlayed += `${eventTime.value?.minutesPlayed}' - `;
        }
    }

    let activePeriodLabel = "";

    if (eventTime.value?.periodNumber && eventTime.value?.periodName) {
        activePeriodLabel = `${eventTime.value?.periodNumber}. ${eventTime.value?.periodName}`;
    }

    let periods = "";

    if (eventPeriods.value?.length) {
        periods += ` - `;
        for (const [index, period] of eventPeriods.value.entries()) {
            if (index === eventPeriods.value.length - 1) {
                periods += `${period.scoreHome}:${period.scoreAway}`;

                periods +=
                    period.pointsHome && period.pointsAway
                        ? `(${period.starHome ? "*" : ""}${period.pointsHome}:${period.pointsAway}${period.starAway ? "*" : ""})`
                        : "";
            } else {
                periods += `${period.scoreHome}:${period.scoreAway}, `;
            }
        }
    }

    return `${score}${timePlayed.length || activePeriodLabel.length || periods.length ? " | " : ""}${timePlayed}${activePeriodLabel}${periods}`;
});
</script>
